import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { useFeatureIsOn } from "@/app/featureFlags";
import { WhatsAppButton } from "@/components/WhatsApp/WhatsAppButton";
import { FlightsList } from "@/features/flights/components/flight-list/FlightsList";
import { useFlightFiltersContext } from "@/features/flights/contexts/FlightFiltersContext";
import { useFlightSearchDataContext } from "@/features/flights/contexts/FlightSearchDataContext";
import { useFlightSelectionPayloadContext } from "@/features/flights/contexts/FlightSelectionPayloadContext/FlightSelectionPayloadContext";
import type { FlightSearchQuery } from "@/features/flights/hooks/flightSearch/types";
import { useDepartureFlightSearch } from "@/features/flights/hooks/flightSearch/useDepartureFlightSearch";
import {
  useFlightQuickSearchTokens,
  useFlightSearchTokens,
} from "@/features/flights/hooks/useFlightSearchTokens";
import {
  TripType,
  type FlightSearchType,
  type FlightType,
  type MappedFlightOffer,
} from "@/features/flights/types/types";
import { FlightTrackingEvents, rudderAnalytics } from "@/lib/rudderstack";

export const Route = createFileRoute(
  "/_authenticated/flights/_offers/departure/list",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const intl = useIntl();

  const isFlightBrandedFaresOn = useFeatureIsOn("flight-branded-fares");

  const { searchData } = useFlightSearchDataContext();
  const { handleDepartureFlightSelection, selectedDepartureFlightId } =
    useFlightSelectionPayloadContext();
  const { query: tokensQuery, cancel: cancelTokensQuery } =
    useFlightSearchTokens();
  const quickTokensQuery = useFlightQuickSearchTokens();
  const { filters } = useFlightFiltersContext();
  const fullSearchQuery = useDepartureFlightSearch(
    tokensQuery,
    filters,
    "full",
  );
  const quickSearchQuery = useDepartureFlightSearch(
    quickTokensQuery,
    null,
    "quick",
  ); // quick search does not use any filters

  const [fullSearchLoaded, setFullSearchLoaded] = useState(
    Boolean(selectedDepartureFlightId),
  );

  const handleFlightSelect = (
    offer: MappedFlightOffer,
    flight: FlightType,
    queryType: FlightSearchType | null,
  ) => {
    rudderAnalytics.track(
      FlightTrackingEvents.USER_SELECTED_PARTICULAR_FLIGHT,
      {
        offer_id: offer.index,
        offer_type: "Flight",
        price: offer.totalPrice,
      },
    );

    handleDepartureFlightSelection(offer, flight.key, queryType);
    if (queryType === "quick") {
      cancelTokensQuery();
    }

    if (searchData?.tripType === TripType.ROUND_TRIP) {
      navigate({
        to: "/flights/return/list",
      });
    } else {
      if (isFlightBrandedFaresOn && offer.upsellSupport) {
        navigate({
          to: "/flights/departure/fare",
        });
      } else {
        navigate({
          to: "/flights/offer/$id/details",
          params: { id: offer.index },
        });
      }
    }
  };

  // we need this to know if full search succeeded in the first place in order to show
  // full search results even if it's refetching (for example becaue of filters change)
  useEffect(() => {
    if (fullSearchQuery.isSuccess) {
      setFullSearchLoaded(true);
    }
  }, [fullSearchQuery.isSuccess]);

  // we need to reset this flag when search data changes
  useEffect(() => {
    const handleSearchDataUpdated: WindowEventHandler<
      "searchdataupdated"
    > = () => {
      setFullSearchLoaded(false);
    };

    window.addEventListener("searchdataupdated", handleSearchDataUpdated);

    return () => {
      window.removeEventListener("searchdataupdated", handleSearchDataUpdated);
    };
  }, []);

  const searchQuery: {
    query: FlightSearchQuery;
    type: FlightSearchType | null;
  } =
    quickSearchQuery.isEmpty || fullSearchLoaded
      ? { query: fullSearchQuery, type: "full" }
      : { query: quickSearchQuery, type: "quick" };

  return (
    <main className="no-footer main h-screen">
      <FlightsList
        onFlightSelect={handleFlightSelect}
        searchQuery={searchQuery}
        direction="departure"
        title={
          searchData?.tripType === TripType.ROUND_TRIP ? (
            <div>
              {intl.formatMessage({
                defaultMessage: "Choose departure flight",
                description:
                  "Page title when selecting the outbound / departure flight",
              })}
            </div>
          ) : null
        }
        isLoadingAdditionalData={
          fullSearchQuery.isFetching || quickSearchQuery.isFetching
        }
      />
      {!searchQuery.query.isLoading && searchQuery.query.isEmpty ? null : (
        <WhatsAppButton />
      )}
    </main>
  );
}
